export interface JUnitTestCase {
  name: string;
  classname: string;
  time: number | null;
  status: "passed" | "failed" | "error" | "skipped";
  message?: string;
  details?: string;
  type?: string;
  systemOut?: string;
  systemErr?: string;
}

export interface JUnitTestSuite {
  name: string;
  tests: number;
  failures: number;
  errors: number;
  skipped: number;
  time: number | null;
  timestamp?: string;
  testCases: JUnitTestCase[];
}

export function parseJUnitXml(xml: string): JUnitTestSuite[] {
  const doc = new DOMParser().parseFromString(xml, "text/xml");

  const parseError = doc.querySelector("parsererror");
  if (parseError) {
    throw new Error(`Invalid XML: ${parseError.textContent}`);
  }

  const root = doc.documentElement;

  if (root.tagName === "testsuites") {
    return directChildren(root, "testsuite").map(parseTestSuite);
  }
  if (root.tagName === "testsuite") {
    return [parseTestSuite(root)];
  }

  throw new Error(`Unexpected root element: <${root.tagName}>. Expected <testsuites> or <testsuite>.`);
}

function directChildren(parent: Element, tagName: string): Element[] {
  const results: Element[] = [];
  for (let i = 0; i < parent.children.length; i++) {
    if (parent.children[i].tagName === tagName) results.push(parent.children[i]);
  }
  return results;
}

function trimmedText(el: Element | null): string | undefined {
  const text = el?.textContent?.trim();
  return text || undefined;
}

function parseNum(val: string | null): number | null {
  if (val == null) return null;
  const n = Number.parseFloat(val);
  return Number.isNaN(n) ? null : n;
}

function parseTestSuite(suite: Element): JUnitTestSuite {
  const testCases = directChildren(suite, "testcase").map(parseTestCase);
  const nestedSuites = directChildren(suite, "testsuite");
  for (const nested of nestedSuites) {
    testCases.push(...directChildren(nested, "testcase").map(parseTestCase));
  }

  const testsAttr = suite.getAttribute("tests");

  return {
    name: suite.getAttribute("name") ?? "(unnamed suite)",
    tests: testsAttr != null ? parseInt(testsAttr, 10) || 0 : testCases.length,
    failures: parseInt(suite.getAttribute("failures") ?? "", 10) || 0,
    errors: parseInt(suite.getAttribute("errors") ?? "", 10) || 0,
    skipped: parseInt(suite.getAttribute("skipped") ?? "", 10) || 0,
    time: parseNum(suite.getAttribute("time")),
    timestamp: suite.getAttribute("timestamp") ?? undefined,
    testCases,
  };
}

function parseTestCase(tc: Element): JUnitTestCase {
  const name = tc.getAttribute("name") ?? "(unnamed)";
  const classname = tc.getAttribute("classname") ?? "";
  const time = parseNum(tc.getAttribute("time"));
  const systemOut = trimmedText(tc.querySelector("system-out"));
  const systemErr = trimmedText(tc.querySelector("system-err"));

  const failures = directChildren(tc, "failure");
  const errors = directChildren(tc, "error");

  if (failures.length > 0) {
    return {
      name,
      classname,
      time,
      status: "failed",
      ...combineResultElements(failures),
      systemOut,
      systemErr,
    };
  }

  if (errors.length > 0) {
    return {
      name,
      classname,
      time,
      status: "error",
      ...combineResultElements(errors),
      systemOut,
      systemErr,
    };
  }

  const skipped = tc.querySelector("skipped");
  if (skipped) {
    return {
      name,
      classname,
      time,
      status: "skipped",
      message: skipped.getAttribute("message") ?? undefined,
      systemOut,
      systemErr,
    };
  }

  return { name, classname, time, status: "passed", systemOut, systemErr };
}

function combineResultElements(elements: Element[]): { message?: string; details?: string; type?: string } {
  if (elements.length === 1) {
    return {
      message: elements[0].getAttribute("message") ?? undefined,
      details: trimmedText(elements[0]),
      type: elements[0].getAttribute("type") ?? undefined,
    };
  }

  const type = elements[0].getAttribute("type") ?? undefined;
  const parts: string[] = [];
  for (const el of elements) {
    const msg = el.getAttribute("message");
    const body = el.textContent?.trim();
    if (msg) parts.push(msg);
    if (body && body !== msg) parts.push(body);
  }
  return { message: elements[0].getAttribute("message") ?? undefined, details: parts.join("\n\n"), type };
}
